// Mercapoli - Customer Orders Page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Loader2, Package } from 'lucide-react';
import CustomerLayout from '../../layouts/CustomerLayout';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { pedidos as pedidosApi } from '../../lib/api';
import { toast } from 'sonner';

const CustomerOrders = () => {
  const [loading, setLoading] = useState(true);
  const [pedidos, setPedidos] = useState([]);
  const [activeTab, setActiveTab] = useState('activos');

  useEffect(() => {
    loadPedidos();
  }, []);

  const loadPedidos = async () => {
    try {
      const data = await pedidosApi.getAll();
      setPedidos(data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      aceptado: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      preparando: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      listo: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
      en_camino: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      entregado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      cancelado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[status] || colors.pendiente;
  };

  const activosEstados = ['pendiente', 'aceptado', 'preparando', 'listo', 'en_camino'];
  const pedidosActivos = pedidos.filter(p => activosEstados.includes(p.estado));
  const pedidosHistorial = pedidos.filter(p => !activosEstados.includes(p.estado));

  const renderPedidoCard = (pedido, index) => (
    <motion.div
      key={pedido.pedido_id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/app/pedido/${pedido.pedido_id}`}
        className="block bg-card rounded-xl border border-border p-4 hover:border-spicy-coral/50 transition-colors"
        data-testid={`order-${pedido.pedido_id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-heading font-bold">Pedido #{pedido.pedido_id.slice(-6)}</p>
            <p className="text-sm text-muted-foreground">
              {new Date(pedido.created_at).toLocaleDateString('es-MX', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <Badge className={getStatusColor(pedido.estado)}>
            {pedido.estado.replace('_', ' ')}
          </Badge>
        </div>

        <div className="space-y-1 mb-3">
          {pedido.items.slice(0, 2).map((item, i) => (
            <p key={i} className="text-sm text-muted-foreground">
              {item.cantidad}x {item.nombre}
            </p>
          ))}
          {pedido.items.length > 2 && (
            <p className="text-sm text-muted-foreground">
              +{pedido.items.length - 2} más
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="font-heading font-bold text-spicy-coral">
            ${pedido.total.toFixed(2)}
          </span>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </Link>
    </motion.div>
  );

  return (
    <CustomerLayout title="Mis pedidos" showSearch={false}>
      <div className="container-app pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="activos" className="relative">
              Activos
              {pedidosActivos.length > 0 && (
                <span className="ml-2 w-5 h-5 rounded-full bg-spicy-coral text-white text-xs flex items-center justify-center">
                  {pedidosActivos.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="historial">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="activos">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
              </div>
            ) : pedidosActivos.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Package className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-bold mb-2">No tienes pedidos activos</h3>
                <p className="text-muted-foreground mb-4">
                  Explora restaurantes y realiza tu primer pedido
                </p>
                <Link to="/app">
                  <button className="text-spicy-coral font-medium hover:underline">
                    Explorar restaurantes
                  </button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {pedidosActivos.map(renderPedidoCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="historial">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
              </div>
            ) : pedidosHistorial.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Package className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-bold mb-2">Sin historial de pedidos</h3>
                <p className="text-muted-foreground">
                  Aquí aparecerán tus pedidos completados
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {pedidosHistorial.map(renderPedidoCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </CustomerLayout>
  );
};

export default CustomerOrders;
